// Facebook Pixel helper — centralised tracking for the entire page
// Pixel ID: 1215304437119773

const PIXEL_ID = '1215304437119773'

/** Safe wrapper — fires only when fbq is loaded */
export function trackFbEvent(event: string, data?: Record<string, any>) {
  if (typeof window !== 'undefined' && (window as any).fbq) {
    ;(window as any).fbq('track', event, data)
  }
}

/** Custom events (use trackCustom instead of track) */
export function trackFbCustomEvent(event: string, data?: Record<string, any>) {
  if (typeof window !== 'undefined' && (window as any).fbq) {
    ;(window as any).fbq('trackCustom', event, data)
  }
}

/** Initialise the pixel — call once on mount */
export function initFbPixel() {
  if (typeof window === 'undefined') return
  if ((window as any).fbq) return // already loaded

  // 1. Load the fbevents.js script FIRST
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  // Use head.appendChild as primary method — guaranteed to work
  // even if no other script tags exist in the DOM
  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }

  // 2. Create the fbq stub that queues calls until the script loads
  const f = window as any
  const n = (f.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
  })
  if (!f._fbq) f._fbq = n
  n.push = n
  n.loaded = true
  n.version = '2.0'
  n.queue = []

  // 3. Queue init + PageView — the script will process these when loaded
  ;(window as any).fbq('init', PIXEL_ID)
  ;(window as any).fbq('track', 'PageView')
}

/** Track when a section becomes visible (fires once per section) */
export function trackSectionViews(sectionMap: Record<string, string>) {
  if (typeof window === 'undefined') return () => {}

  const fired = new Set<string>()
  const observers: IntersectionObserver[] = []

  // Small delay to ensure DOM is ready
  const timeoutId = setTimeout(() => {
    Object.entries(sectionMap).forEach(([selector, sectionName]) => {
      const el = document.querySelector(selector)
      if (!el) return

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !fired.has(sectionName)) {
            fired.add(sectionName)
            trackFbCustomEvent('SectionView', {
              section_name: sectionName,
              content_category: 'Kinesiología Carrera Resuelta',
            })
            observer.disconnect()
          }
        },
        { threshold: 0.3 },
      )
      observer.observe(el)
      observers.push(observer)
    })
  }, 500)

  return () => {
    clearTimeout(timeoutId)
    observers.forEach((o) => o.disconnect())
  }
}

/** Track scroll depth milestones (25%, 50%, 75%, 100%) */
export function trackScrollDepth() {
  if (typeof window === 'undefined') return () => {}

  const milestones = [25, 50, 75, 100]
  const fired = new Set<number>()

  const handler = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const docHeight = document.documentElement.scrollHeight - window.innerHeight
    if (docHeight <= 0) return
    const percent = Math.round((scrollTop / docHeight) * 100)

    milestones.forEach((m) => {
      if (percent >= m && !fired.has(m)) {
        fired.add(m)
        trackFbCustomEvent('ScrollDepth', {
          depth_percent: m,
          content_category: 'Kinesiología Carrera Resuelta',
        })
      }
    })
  }

  window.addEventListener('scroll', handler, { passive: true })
  return () => window.removeEventListener('scroll', handler)
}

/** Track time on page milestones (30s, 60s, 120s, 300s) */
export function trackTimeOnPage() {
  if (typeof window === 'undefined') return () => {}

  const milestones = [30, 60, 120, 300]
  const timers: ReturnType<typeof setTimeout>[] = []

  milestones.forEach((seconds) => {
    const timer = setTimeout(() => {
      trackFbCustomEvent('TimeOnPage', {
        seconds,
        content_category: 'Kinesiología Carrera Resuelta',
      })
    }, seconds * 1000)
    timers.push(timer)
  })

  return () => timers.forEach((t) => clearTimeout(t))
}
